import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { ReadMode, SapOdpSettings } from 'app/model/dataflow/dataset-settings/sap-odp-settings';
import { DataFlowDSLAdapterRegistry, Metamodel, Owned, ReadBehavior } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { BaseFormatAdapter } from './base-format.adapter';
import { StringDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(SapOdpSettings)
})
export class SapOdpAdapter extends BaseFormatAdapter<SapOdpSettings> {
    protected async toSourceDSL(sapOdpSettings: SapOdpSettings): Promise<string[]> {
        const props = [`store: '${sapOdpSettings.getFormat()}'`, `format: 'sapObject'`, 'staged: true'];
        if (sapOdpSettings.context.expression) {
            props.push(`context: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sapOdpSettings.context)}`);
        }
        if (sapOdpSettings.objectName.expression) {
            props.push(`objectName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sapOdpSettings.objectName)}`);
        }
        props.push(`readMode: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sapOdpSettings.readMode)}`);
        if (sapOdpSettings.legacyMode && !sapOdpSettings.readMode.templateExpression) {
            props.push(`enableCdc: ${sapOdpSettings.readMode.expression !== ReadMode.FULL_LOAD}`);
        }
        if (sapOdpSettings.runMode !== ReadBehavior.FULL_ON_EVERY_RUN) {
            props.push(
                `keys: ${DataFlowExpressionUtils.wrapExpressionArrayConfigValue(sapOdpSettings.sapOdpKeyColumns)}`
            );
        }
        if (sapOdpSettings.fixDecimalPrecision) {
            props.push(`fixDecimalPrecision: ${sapOdpSettings.fixDecimalPrecision}`);
        }
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<SapOdpSettings> {
        const result = Owned.create<SapOdpSettings>(SapOdpSettings.MODEL, owner);
        result.initialize(undefined);
        if (configs.has('context')) {
            handledConfigs.push('context');
            result.context = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('context'), owner);
        }
        if (configs.has('objectName')) {
            handledConfigs.push('objectName');
            result.objectName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('objectName'), owner);
        }
        if (configs.has('enableCdc')) {
            result.legacyMode = true;
        }
        if (configs.has('readMode')) {
            handledConfigs.push('readMode');
            result.readMode = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('readMode'), owner);
        }
        if (configs.has('keys')) {
            result.sapOdpKeyColumns = DataFlowExpressionUtils.unwrapExpressionArrayConfigValue(
                configs.get('keys'),
                owner,
                StringDataFlowType.TYPE
            );
        }
        if (!configs.has('fixDecimalPrecision')) {
            result.fixDecimalPrecision = false;
        }
        return result;
    }
}
